import React from 'react';
import { Input } from 'antd';
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarCircleOutlined,
  CheckOutlined,
  UserOutlined,
  CalendarOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { TextArea } = Input;

const ClaimActionButtons = ({
  data,
  isPending,
  submitting,
  observations,
  setObservations,
  onSimulate,
  onApprove,
  onRejectClick,
  onPaymentClick
}) => {
  const isPaid = data.status === 'Pagado';

  const getStatusTimestamp = (stepName) => {
    if (!data.statusHistory || !Array.isArray(data.statusHistory)) {
      return '--/--/---- --:--';
    }
    const log = data.statusHistory.find(h => (h.status || '').toLowerCase() === stepName.toLowerCase());
    if (log && log.createdAt) {
      return dayjs(log.createdAt).format('DD/MM/YYYY hh:mm a');
    }
    return '--/--/---- --:--';
  };

  const getPaymentAdmin = () => {
    if (!data.statusHistory || !Array.isArray(data.statusHistory)) {
      return 'Administrador';
    }
    const log = data.statusHistory.find(h => (h.status || '').toLowerCase() === 'pagado');
    if (log && log.user) {
      return `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim() || 'Administrador';
    }
    return 'Administrador';
  };

  if (isPaid) {
    return (
      <div style={{ 
        marginTop: '24px',
        backgroundColor: 'rgba(34, 197, 94, 0.1)', 
        border: '1px solid rgba(34, 197, 94, 0.3)', 
        borderRadius: '12px', 
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '50%', 
            backgroundColor: 'rgba(34, 197, 94, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <CheckOutlined style={{ color: '#22c55e', fontSize: '24px' }} />
          </div>
          <div>
            <h3 style={{ color: '#22c55e', margin: 0, fontSize: '18px', fontWeight: 600 }}>Pago Registrado Exitosamente</h3>
            <p style={{ color: '#94a3b8', margin: 0, marginTop: '4px', fontSize: '14px' }}>El proceso de reclamo de premio ha sido completado.</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
          {data.receiptUrl && (
            <a 
              href={data.receiptUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: 'rgba(234, 179, 8, 0.15)',
                border: '1px solid rgba(234, 179, 8, 0.4)',
                borderRadius: '8px',
                color: '#facc15',
                fontWeight: 600,
                fontSize: '14px',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
            >
              <FilePdfOutlined style={{ fontSize: '18px', color: '#ef4444' }} /> Ver Comprobante (PDF)
            </a>
          )}
          <div>
            <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CalendarOutlined /> Fecha de Registro
            </div>
            <div style={{ color: '#f8fafc', fontWeight: 500 }}>{getStatusTimestamp('Pagado')}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <UserOutlined /> Registrado por
            </div>
            <div style={{ color: '#f8fafc', fontWeight: 500 }}>{getPaymentAdmin()}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-actions-section">
      <div className="claim-observations">
        {data.status === 'Corrección Solicitada' && data.rejectReason && (
          <div style={{ marginBottom: 24 }}>
            <label style={{ color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExclamationCircleOutlined /> Motivo de la corrección solicitada al participante
            </label>
            <div style={{ 
              padding: '12px 16px', 
              backgroundColor: 'rgba(245, 158, 11, 0.05)', 
              borderRadius: '8px', 
              border: '1px solid rgba(245, 158, 11, 0.2)',
              color: '#f8fafc',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
              marginTop: '10px'
            }}>
              {data.rejectReason}
            </div>
          </div>
        )}

        <label>Observaciones <span>(opcionales)</span></label>
        <TextArea 
          rows={2} 
          className="claim-textarea" 
          placeholder="Agrega notas internas sobre esta validación..."
          maxLength={500}
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
        />
        <div className="claim-char-count">{observations.length} / 500 caracteres</div>
      </div>

      <div className="claim-action-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Botón de simulación oculto de la vista pero conservado para pruebas posteriores */}
        {isPending && (
          <button 
            className="claim-action-btn" 
            onClick={onSimulate}
            disabled={submitting}
            style={{ backgroundColor: '#0ea5e9', borderColor: '#0ea5e9' }}
          >
            <InfoCircleOutlined className="btn-icon" />
            <div className="btn-text-group">
              <span className="btn-title">Simular App Móvil</span>
              <span className="btn-subtitle">Llena el formulario para continuar flujo</span>
            </div>
          </button>
        )}

        <div className="claim-buttons-container">
          {data.status === 'Aprobado para Pago' ? (
            <button 
              className="claim-action-btn btn-payment" 
              onClick={onPaymentClick}
              disabled={submitting}
              style={{ backgroundColor: '#eab308', borderColor: '#eab308', color: '#000', width: '100%', justifyContent: 'center', height: '56px' }}
            >
              <DollarCircleOutlined className="btn-icon" style={{ color: '#000' }} />
              <span className="btn-title" style={{ fontSize: '18px', fontWeight: 'bold' }}>Registrar Pago</span>
            </button>
          ) : (
            <>
              <button 
                className="claim-action-btn btn-approve" 
                onClick={onApprove}
                disabled={submitting || isPending}
                style={{ opacity: (submitting || isPending) ? 0.5 : 1 }}
              >
                <CheckCircleOutlined className="btn-icon" />
                <div className="btn-text-group">
                  <span className="btn-title">Aprobar Información</span>
                  <span className="btn-subtitle">La información es correcta</span>
                </div>
              </button>
              
              <button 
                className="claim-action-btn btn-reject" 
                onClick={onRejectClick}
                disabled={submitting || isPending}
                style={{ opacity: (submitting || isPending) ? 0.5 : 1 }}
              >
                <ExclamationCircleOutlined className="btn-icon" />
                <div className="btn-text-group">
                  <span className="btn-title">Solicitar Corrección</span>
                  <span className="btn-subtitle">La información tiene errores o está incompleta</span>
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimActionButtons;
